import {
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  useTheme,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';

import {
  CustomAvatar,
  FormProvider,
  GlassEffectDialog,
  Iconify,
  RHFAutocomplete,
  Scrollbar,
} from '@app/components';
import { IAddTaskBoardSchema, ISearchMember } from '../types';
import {
  selectAddMemberState,
  selectBoardDetailsState,
  selectDeleteMemberState,
  selectSearchMemberState,
  selectUpdateMemberState,
} from '../selector';
import { useRequestState } from '@app/hooks';
import { SvgColor } from '@app/components/svg-color';
import { Strings } from '@app/constants';
import { addMember, deleteMember, getSearchMember } from '../slice';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  iconName: string;
}
interface IMember {
  id: number;
  label: string;
  firstName: string;
  lastName: string;
  profileImage?: any;
  membersUserId?: number;
  isAdmin?: boolean;
  roleId?: number | string;
}
const AddMemberDialog = ({ open, onClose, iconName }: Props) => {
  const methods = useForm<IAddTaskBoardSchema>();
  const dispatch = useDispatch();

  const { data } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });
  const theme = useTheme();

  const { data: member } = useRequestState({
    stateSelector: selectSearchMemberState,
  });

  const { isSuccess: isAddMemberSuccess } = useRequestState({
    stateSelector: selectAddMemberState,
    errorShown: true,
    successMessageShown: true,
  });

  const [query, setQuery] = useState<any>('');
  const [selectedMember, setSelectedMember] = useState<any[]>([]);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const searchedMember: ISearchMember = methods.watch('searchMember');

  const renderMemberList = (members: IMember[], title: string, icon: React.ReactNode) => {
    return (
      <>
        <Stack direction="row" gap={1} mt={2}>
          {icon}
          <Stack color={theme.palette.grey[600]} fontSize={20}>
            {title}
          </Stack>
        </Stack>
        <List>
          {members.map((member) => (
            <ListItem
              key={member?.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                px: 0,
                gap: 2,
              }}
            >
              <ListItemAvatar
                sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}
              >
                <CustomAvatar
                  name={member?.label}
                  sx={{
                    bgcolor: theme.palette.taskboard.raisinBlack,
                    color: theme.palette.error.contrastText,
                    fontSize: 14,
                  }}
                  src={member?.profileImage}
                />
                <ListItemText primary={member?.label} />
              </ListItemAvatar>
              {!member?.isAdmin && member?.roleId !== 1 && (
                <Button
                  sx={{
                    color: theme.palette.taskboard.raisinBlack,
                    textDecoration: 'underline',
                    '&:hover': {
                      textDecoration: 'underline',
                      background: 'none',
                    },
                  }}
                  onClick={() => {
                    handleDelete(member?.id);
                  }}
                >
                  {Strings.taskBoard.removeFromBoard}
                </Button>
              )}
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  useEffect(() => {
    const transformedMembers = data?.members?.map((member: IMember) => ({
      label: `${member.firstName} ${member.lastName}`,
      id: member?.membersUserId,
      profileImage: member?.profileImage,
      isAdmin: member?.isAdmin ?? false,
      roleId: member?.roleId,
    }));
    setSelectedMember(transformedMembers);
  }, [data]);

  useEffect(() => {
    if (isAddMemberSuccess) {
      methods.setValue('searchMember', { label: '', id: 0 });
      setQuery('');
      setAutocompleteKey((prev) => prev + 1);
    }
  }, [isAddMemberSuccess, methods]);

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      if (data?.projectId !== undefined) {
        dispatch(getSearchMember({ name: query, boardId: data?.projectId }));
      }
    }, 500);

    debouncedSearch();

    return () => {
      debouncedSearch.cancel();
    };
  }, [query, data?.projectId, dispatch]);

  useEffect(() => {
    if (open && data?.projectId !== undefined) {
      dispatch(getSearchMember({ name: '', boardId: data?.projectId }));
    }
  }, [open, data?.projectId, dispatch]);

  useRequestState({
    stateSelector: selectDeleteMemberState,
    errorShown: true,
  });

  const handleDelete = (userId: number) => {
    dispatch(deleteMember({ userId, boardId: data?.id }));
  };

  useRequestState({
    stateSelector: selectUpdateMemberState,
    errorShown: true,
  });

  const handleAddMember = () => {
    const value: any = searchedMember;
    if (value?.id) {
      dispatch(
        addMember({
          membersUserIds: value?.id,
          boardId: data?.id,
        })
      );
    }
  };

  return (
    <>
      <FormProvider methods={methods}>
        <GlassEffectDialog
          open={open}
          onClose={onClose}
          title={'Add Member'}
          backgroundImage={data?.image}
          boardIcon={iconName}
        >
          <Stack sx={{ gap: 1 }}>
            <Stack sx={{ width: '100%' }} mt={-2}>
              <RHFAutocomplete
                key={autocompleteKey}
                name={'searchMember'}
                getOptionLabel={(option: any) => option.label || ''}
                options={
                  member?.records
                    ?.filter(
                      (option: IMember) => !selectedMember?.some((m) => m?.id === option?.id)
                    )
                    ?.map((option: IMember) => ({
                      label: `${option?.firstName} ${option?.lastName}`,
                      id: option?.id,
                    })) ?? []
                }
                onInputChange={(event: any, newInputValue: string) => {
                  setQuery(newInputValue);
                }}
                label={Strings.common.search}
              />
            </Stack>
            {searchedMember?.label?.length > 0 && (
              <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{
                    backgroundColor: theme.palette.taskboard.raisinBlack,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.taskboard.raisinBlack,
                      color: theme.palette.error.contrastText,
                    },
                    width: '20%',
                    ml: 'auto',
                  }}
                  onClick={handleAddMember}
                >
                  {Strings.taskBoard.add}
                </Button>
              </Stack>
            )}
          </Stack>
          <Scrollbar>
            <Scrollbar>
              {renderMemberList(
                selectedMember?.filter((m) => m?.isAdmin || m?.roleId === 1),
                Strings.taskBoard.admin,
                <Iconify
                  icon="mdi:crown"
                  style={{ width: 25, height: 25, color: theme.palette.grey[600] }}
                />
              )}
              {renderMemberList(
                selectedMember?.filter((m) => !m?.isAdmin && m?.roleId !== 1),
                Strings.taskBoard.addedMembers,
                <SvgColor
                  src={`/assets/icons/card/ic_addedMember.svg`}
                  sx={{ width: 25, height: 25, color: theme.palette.grey[600] }}
                />
              )}
            </Scrollbar>
          </Scrollbar>
        </GlassEffectDialog>
      </FormProvider>
    </>
  );
};

export { AddMemberDialog };
